import { App, Descriptions, Form, Modal, Select, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { ProjectDepartmentSelector } from '@/pages/admin/projects/ProjectDepartmentSelector';
import { toManagerSelectOptions } from '@/pages/admin/projects/project-manager-options';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectDepartmentMigrationSuggestion, ProjectListItem } from '@/types/project';

interface ProjectDepartmentMigrationModalProps {
  open: boolean;
  project: ProjectListItem | null;
  onClose: () => void;
  onConfirmed: () => void;
}

export function ProjectDepartmentMigrationModal({
  open,
  project,
  onClose,
  onConfirmed,
}: ProjectDepartmentMigrationModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{
    departmentIds: string[];
    projectManagerId: string;
  }>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suggestion, setSuggestion] = useState<ProjectDepartmentMigrationSuggestion | null>(null);
  const [managerOptions, setManagerOptions] = useState<{ value: string; label: string }[]>([]);
  const [managerLoading, setManagerLoading] = useState(false);

  const departmentIds = Form.useWatch('departmentIds', form) ?? [];

  useEffect(() => {
    if (!open || !project) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSuggestion(null);

    void (async () => {
      try {
        const data = await projectsApi.fetchDepartmentMigrationSuggestion(project.id);
        if (cancelled) {
          return;
        }
        setSuggestion(data);
        form.setFieldsValue({
          departmentIds: data.suggestedDepartmentIds,
          projectManagerId: data.currentProjectManagerId,
        });
      } catch (error) {
        if (!cancelled) {
          const text = error instanceof ApiError ? error.message : '加载迁移建议失败';
          message.error(text);
          onClose();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form, message, onClose, open, project]);

  useEffect(() => {
    if (!open || !project || departmentIds.length === 0) {
      setManagerOptions([]);
      return;
    }

    let cancelled = false;
    setManagerLoading(true);

    void (async () => {
      try {
        const result = await projectsApi.fetchProjectManagerCandidates(project.id, departmentIds);
        if (cancelled) {
          return;
        }
        setManagerOptions(toManagerSelectOptions(result.items));
        const currentManagerId = form.getFieldValue('projectManagerId') as string | undefined;
        if (currentManagerId && !result.items.some((item) => item.userId === currentManagerId)) {
          form.setFieldValue('projectManagerId', undefined);
        }
      } catch {
        if (!cancelled) {
          setManagerOptions([]);
        }
      } finally {
        if (!cancelled) {
          setManagerLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [departmentIds, form, open, project]);

  const handleConfirm = async () => {
    if (!project) {
      return;
    }

    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const result = await projectsApi.confirmProjectDepartments(project.id, {
        departmentIds: values.departmentIds,
        projectManagerId: values.projectManagerId,
      });
      message.success(`已配置项目部门，当前成员 ${result.memberSync.total} 人`);
      onConfirmed();
      onClose();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '确认迁移失败';
      message.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="配置项目部门（迁移）"
      open={open}
      width={640}
      onCancel={onClose}
      destroyOnClose
      confirmLoading={submitting}
      okText="确认并同步成员"
      onOk={() => void handleConfirm()}
    >
      {project ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            项目：{project.name}（{project.projectNo}）
          </Typography.Text>

          {suggestion ? (
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="当前成员数">{suggestion.currentMemberCount}</Descriptions.Item>
              <Descriptions.Item label="同步后预估成员数">
                {suggestion.estimatedMemberCountAfterSync}
              </Descriptions.Item>
              <Descriptions.Item label="说明">{suggestion.rationale}</Descriptions.Item>
            </Descriptions>
          ) : null}

          <Form form={form} layout="vertical" disabled={loading}>
            <Form.Item
              name="departmentIds"
              label="项目部门"
              rules={[
                {
                  validator: async (_, ids: string[] | undefined) => {
                    if (!ids || ids.length === 0) {
                      throw new Error('请至少添加一个项目部门');
                    }
                  },
                },
              ]}
            >
              <ProjectDepartmentSelector disabled={loading} />
            </Form.Item>

            <Form.Item
              name="projectManagerId"
              label="项目负责人"
              rules={[{ required: true, message: '请选择项目负责人' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={managerOptions}
                loading={managerLoading}
                disabled={departmentIds.length === 0}
                placeholder={departmentIds.length === 0 ? '请先添加项目部门' : '请选择项目负责人'}
              />
            </Form.Item>
          </Form>
        </Space>
      ) : null}
    </Modal>
  );
}
